import Layout from "@/components/Layout/Layout"
import React, { useEffect } from "react"
import { ArrowUpCircle } from "lucide-react"

export default function TournamentLegal() {
  useEffect(() => {
    const links = document.querySelectorAll(".scroll-link")
    links.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault()
        const targetId = link.getAttribute("href")?.replace("#", "")
        const target = document.getElementById(targetId)
        if (target) {
          window.scrollTo({ top: target.offsetTop - 80, behavior: "smooth" })
        }
      })
    })
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <Layout>
      <main className="max-w-6xl mx-auto p-6 space-y-10" id="top">
        <h1 className="text-4xl font-extrabold text-center bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
          Términos y Condiciones • Política de Privacidad
        </h1>

        <nav className="fixed right-5 top-1/3 bg-white/80 dark:bg-gray-900/80 backdrop-blur- rounded-2xl shadow-lg p-3 space-y-2 z-50 hidden md:block">
          <a href="#terminos" className="scroll-link block text-sm hover:text-indigo-500">
            📜 Términos de uso
          </a>
          <a href="#privacidad" className="scroll-link block text-sm hover:text-indigo-500">
            🔒 Política de privacidad
          </a>
          <a href="#conducta" className="scroll-link block text-sm hover:text-indigo-500">
            🎮 Código de conducta
          </a>
          <a href="#clasificacion" className="scroll-link block text-sm hover:text-indigo-500">
            🏆 Clasificación y puntuaciones
          </a>
          <a href="#datos" className="scroll-link block text-sm hover:text-indigo-500">
            🗂️ Datos personales y retención
          </a>
          <a href="#responsabilidades" className="scroll-link block text-sm hover:text-indigo-500">
            ⚖️ Responsabilidad del usuario
          </a>
          <a href="#consentimiento" className="scroll-link block text-sm hover:text-indigo-500">
            ✍️ Consentimiento del usuario
          </a>
          <a href="#vigencia" className="scroll-link block text-sm hover:text-indigo-500">
            📅 Vigencia y modificaciones
          </a>
          <a href="#contacto" className="scroll-link block text-sm hover:text-indigo-500">
            📧 Contacto
          </a>
          <a
            href="#top"
            className="scroll-link block text-xs text-gray-400 hover:text-indigo-500 mt-3 border-t pt-2"
          >
            ⬆️ Volver arriba
          </a>
        </nav>

        <LegalTerms scrollToTop={scrollToTop} />
      </main>
    </Layout>
  )
}

export const LegalTerms = ({ scrollToTop = null, className = "" }) => (
  <div className={className}>
    <section id="terminos" className={"prose max-w-none"}>
      <h2 className="text-2xl my-4 font-bold text-indigo-500">📜 Términos de Uso</h2>
      <p>
        Estos Términos de Uso regulan la utilización del{" "}
        <strong>Sistema de Torneos de Mitos y Leyendas</strong>. Al crear una cuenta, registrarte en
        un torneo o usar el Sistema, aceptas estos términos y cualquier actualización futura.
      </p>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">
        Acceso y cuentas
      </h3>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>
          Debes ser mayor de edad según la jurisdicción aplicable o contar con autorización de tu
          representante legal.
        </li>
        <li>Proporcionarás información veraz y mantendrás actualizados los datos de tu cuenta.</li>
        <li>
          Eres responsable de la confidencialidad de tus credenciales y de todas las acciones
          realizadas con tu cuenta.
        </li>
      </ul>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">
        Comportamiento aceptable
      </h3>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>
          No se permite conducta fraudulenta, trampas, abuso, hostigamiento ni suplantación de
          identidad.
        </li>
        <li>
          Queda prohibido manipular resultados, crear cuentas múltiples con fines deshonestos o
          explotar errores técnicos para obtener ventaja.
        </li>
      </ul>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">
        Modificaciones y suspensión
      </h3>
      <p>
        El Sistema puede modificar, suspender o discontinuar funcionalidades, reglas o contenidos.
        También nos reservamos el derecho de suspender o eliminar cuentas que infrinjan estos
        términos.
      </p>
    </section>

    <section id="privacidad" className="prose max-w-none">
      <h2 className="text-2xl my-4 font-bold text-indigo-500">🔒 Política de Privacidad</h2>
      <p>
        El <strong>Sistema de Torneos de Mitos y Leyendas</strong> garantiza la protección total de
        los datos personales de los usuarios. No se utilizan servicios de terceros; toda la
        información se maneja de manera interna, segura y conforme a la legislación vigente.
      </p>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">
        Datos que recopilamos
      </h3>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>Información de cuenta: nombre, correo electrónico y datos de perfil.</li>
        <li>
          Datos de uso: actividad en torneos, resultados, historial de partidas y métricas de
          rendimiento.
        </li>
        <li>
          Datos técnicos: dirección IP, información del dispositivo, registros de sesión y cookies.
        </li>
      </ul>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">Finalidades</h3>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>Proveer y mejorar el Sistema y los torneos.</li>
        <li>Prevenir fraudes y hacer cumplir las reglas.</li>
        <li>Comunicaciones operativas (notificaciones de torneos, cambios y soporte).</li>
      </ul>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">Seguridad</h3>
      <p>
        Implementamos medidas razonables para proteger tus datos. Recomendamos proteger tus
        credenciales y usar contraseñas únicas.
      </p>
    </section>

    <section id="conducta" className="prose max-w-none">
      <h2 className="text-2xl my-4 font-bold text-indigo-500">🎮 Código de Conducta</h2>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>Respeta a otros jugadores, jueces y organizadores.</li>
        <li>No se permite fraude, trampas o manipulación de resultados.</li>
        <li>Reporta incidentes o conductas inapropiadas al equipo de soporte.</li>
      </ul>
    </section>

    <section id="clasificacion" className="prose max-w-none">
      <h2 className="text-2xl my-4 font-bold text-indigo-500">🏆 Clasificación y Puntuaciones</h2>
      <p>
        Los algoritmos que gestionan emparejamientos, puntos y clasificaciones son automáticos. Si
        detectas un error, repórtalo mediante el procedimiento de contacto.
      </p>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>Los resultados y rankings son oficiales y administrados internamente.</li>
        <li>Los jugadores pueden revisar su historial dentro de su cuenta.</li>
        <li>No se permite manipulación de resultados sin autorización.</li>
      </ul>
    </section>

    <section id="datos" className="prose max-w-none gap-4 grid">
      <h2 className="text-2xl my-4 font-bold text-indigo-500">
        🗂️ Tratamiento de Datos Personales para Torneos Oficiales de Mitos y Leyendas
      </h2>

      <p>
        <b>Fenix Entertainment SpA</b>, RUT <b>76.115.338-2</b>, con domicilio en{" "}
        <b>José Manuel Infante 1251, comuna de Providencia, Santiago de Chile,</b> es el responsable
        del tratamiento de los datos personales recolectados a través del sistema de inscripción de
        torneos disponible en https://torneos.myl.cl (en adelante, el “Sistema de Torneos MyL”).
      </p>

      <p>
        La recolección de datos personales —incluyendo <b>nombre, apellido y RUT</b>— tiene como
        finalidad:
      </p>

      <ul className="list-disc pl-6 space-y-2 leading-relaxed marker:text-blue-600">
        <li>
          Registrar y validar la participación de los jugadores en torneos oficiales de{" "}
          <b>Mitos y Leyendas</b>.
        </li>
        <li>
          Verificar la identidad de los jugadores para evitar duplicaciones, suplantaciones o
          fraudes en los procesos de inscripción y participación.
        </li>
        <li>
          Validar la entrega de premios físicos o digitales asociados a la participación en dichos
          torneos.
        </li>
      </ul>

      <p>
        Los datos serán almacenados{" "}
        <b>durante la vigencia del torneo y hasta 12 meses posteriores a la inscripción</b>, o hasta
        que el jugador solicite su eliminación o baja del Sistema de Torneos, lo que ocurra primero.
      </p>

      <p>
        El tratamiento de estos datos se efectuará conforme a lo dispuesto en la{" "}
        <b>Ley Nº 19.628 sobre Protección de la Vida Privada</b>, garantizando la confidencialidad,
        integridad y seguridad de la información.
      </p>

      <p>
        Los datos personales <b>no serán compartidos con terceros ajenos a la organización</b>, y su
        uso quedará estrictamente limitado a la administración de torneos oficiales de{" "}
        <b>Mitos y Leyendas</b> y de sus <b>tiendas oficiales asociadas</b>, incluyendo eventos como{" "}
        <b>lanzamientos, ligas, torneos casuales, Premier y Nacionales</b>, entre otros.
      </p>

      <p>
        Los jugadores podrán ejercer sus derechos de{" "}
        <b>acceso, rectificación, cancelación y oposición (ARCO)</b>, así como solicitar la
        eliminación de sus datos personales del sistema, contactando a <b>jo@myl.cl</b>.
      </p>

      <p>
        Mediante la participación en el torneo o el uso del Sistema de Torneos MyL, el jugador{" "}
        <b>autoriza expresamente a Fenix Entertainment SpA</b> para el tratamiento de sus datos
        personales conforme a las finalidades aquí descritas.
      </p>
    </section>

    <section id="responsabilidades" className="prose max-w-none">
      <h2 className="text-2xl my-4 font-bold text-indigo-500">⚖️ Responsabilidad del Usuario</h2>
      <p>
        El uso del Sistema implica la aceptación plena de las obligaciones descritas en este
        documento. Cada usuario asume responsabilidad individual por:
      </p>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>
          <strong>Su cuenta:</strong> mantener la seguridad de sus credenciales y notificar de
          inmediato cualquier acceso no autorizado.
        </li>
        <li>
          <strong>Sus acciones:</strong> toda actividad realizada desde su cuenta se considera
          responsabilidad directa del titular, incluso si fue realizada por un tercero con acceso
          otorgado por el propio usuario.
        </li>
        <li>
          <strong>La veracidad de su información:</strong> datos de perfil, resultados reportados y
          cualquier contenido enviado al Sistema deben ser precisos y actualizados.
        </li>
        <li>
          <strong>El respeto a otros participantes:</strong> conductas que afecten la integridad del
          torneo, el buen trato entre jugadores o el funcionamiento del Sistema podrán ser
          sancionadas.
        </li>
      </ul>
      <h3 className="text-2xl font-semibold mb-4 mt-2 leading-snug text-gray-800">Sanciones</h3>
      <p>
        El incumplimiento de cualquiera de las normas establecidas puede derivar en advertencias,
        suspensión temporal o eliminación permanente de la cuenta, a discreción del equipo
        administrador, sin perjuicio de las acciones legales que pudieran corresponder.
      </p>
    </section>

    <section id="consentimiento" className="prose max-w-none">
      <h2 className="text-2xl my-4 font-bold text-indigo-500">✍️ Consentimiento del Usuario</h2>
      <p>
        Al crear una cuenta, inscribirte en un torneo o utilizar cualquier función del Sistema,
        manifiestas de manera libre, informada e inequívoca tu consentimiento para:
      </p>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>
          El tratamiento de tus datos personales conforme a la{" "}
          <strong>Política de Privacidad</strong> descrita en este documento.
        </li>
        <li>
          La publicación de tu nombre, código de jugador, resultados y posición en rankings dentro
          de la plataforma, con el fin de reflejar el estado competitivo oficial.
        </li>
        <li>
          La recepción de comunicaciones operativas relacionadas con torneos, cambios en el Sistema
          y soporte técnico.
        </li>
        <li>
          La aplicación de las reglas del juego y del Sistema, incluyendo sanciones en caso de
          infracción.
        </li>
      </ul>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">
        Revocación del consentimiento
      </h3>
      <p>
        Puedes revocar tu consentimiento en cualquier momento solicitando la eliminación de tu
        cuenta y datos personales a <strong>jo@myl.cl</strong>. Ten en cuenta que la revocación
        puede implicar la imposibilidad de continuar participando en torneos activos o acceder al
        historial competitivo.
      </p>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">
        Menores de edad
      </h3>
      <p>
        Si el usuario es menor de edad según la legislación aplicable, el registro y uso del Sistema
        requiere autorización expresa de su representante legal, quien asume corresponsabilidad por
        el cumplimiento de estos términos.
      </p>
    </section>

    <section id="vigencia" className="prose max-w-none">
      <h2 className="text-2xl my-4 font-bold text-indigo-500">📅 Vigencia y Modificaciones</h2>
      <p>
        Estos Términos y Condiciones entran en vigor desde el momento en que el usuario crea una
        cuenta o hace uso del Sistema, y se mantienen vigentes mientras la cuenta permanezca activa
        o hasta que sean reemplazados por una versión actualizada.
      </p>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">
        Actualización de los términos
      </h3>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>
          El Sistema se reserva el derecho de modificar estos términos en cualquier momento para
          adaptarse a cambios legales, operativos o de producto.
        </li>
        <li>
          Los cambios relevantes serán notificados a través de la plataforma con al menos{" "}
          <strong>7 días de anticipación</strong> antes de entrar en vigor.
        </li>
        <li>
          El uso continuado del Sistema tras la notificación implica la aceptación de las
          modificaciones realizadas.
        </li>
        <li>
          En caso de no aceptar los nuevos términos, el usuario podrá solicitar la eliminación de su
          cuenta antes de la fecha de vigencia.
        </li>
      </ul>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">
        Versiones anteriores
      </h3>
      <p>
        Las versiones anteriores de estos términos quedan sin efecto al momento de publicarse una
        actualización. La fecha de última modificación se indica al pie de este documento.
      </p>
    </section>

    <section id="contacto" className="prose max-w-none">
      <h2 className="text-2xl my-4 font-bold text-indigo-500">📧 Contacto</h2>
      <p>
        Para cualquier consulta, reclamo o solicitud relacionada con el funcionamiento del Sistema,
        los datos personales o estos Términos y Condiciones, puedes comunicarte con el equipo a
        través de los siguientes medios:
      </p>
      <ul className="list-disc list-inside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
        <li>
          <strong>Soporte general y técnico:</strong>{" "}
          <a href="mailto:jo@klu.cl" className="text-indigo-500 hover:underline">
            jo@klu.cl
          </a>
        </li>
        <li>
          <strong>Solicitudes ARCO (acceso, rectificación, cancelación y oposición):</strong>{" "}
          <a href="mailto:jo@myl.cl" className="text-indigo-500 hover:underline">
            jo@myl.cl
          </a>
        </li>
        <li>
          <strong>Reportes de conducta o irregularidades en torneos:</strong> a través del
          formulario disponible en la plataforma o al correo de soporte.
        </li>
      </ul>
      <p>
        Nos comprometemos a responder toda solicitud en un plazo máximo de{" "}
        <strong>5 días hábiles</strong> desde su recepción.
      </p>
      <h3 className="text-xl font-semibold mb-4 mt-2 leading-snug text-gray-800">
        Responsable del tratamiento
      </h3>
      <p>
        <strong>Fenix Entertainment SpA</strong>
        <br />
        RUT: 76.115.338-2
        <br />
        Domicilio: José Manuel Infante 1251, Providencia, Santiago de Chile
        <br />
        Correo:{" "}
        <a href="mailto:jo@myl.cl" className="text-indigo-500 hover:underline">
          jo@myl.cl
        </a>
      </p>
    </section>

    {scrollToTop && (
      <button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full p-3 shadow-lg transition-all duration-300"
      >
        <ArrowUpCircle className="h-6 w-6" />
      </button>
    )}

    <footer className="text-sm text-center text-gray-500 dark:text-gray-400 py-6">
      <p>
        Última actualización:{" "}
        {new Date("2026-05-03").toLocaleDateString("es-CL", {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        })}
        .
      </p>
      <p>
        Este documento refleja el compromiso del Sistema de Torneos de Mitos y Leyendas con
        privacidad, seguridad y transparencia para sus usuarios.
      </p>
    </footer>
  </div>
)
